import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  ShoppingCart, Loader2, AlertCircle, Plus, Pencil, Trash2, X, Search,
  Package, Tags, Upload, Info, Send,
} from 'lucide-react';
import {
  listShopCategoriesAdmin, createShopCategory, updateShopCategory, deleteShopCategory,
  listShopProductsAdmin, createShopProduct, updateShopProduct, deleteShopProduct,
  listShopOptionsAdmin, createShopOption, updateShopOption, deleteShopOption,
  uploadShopImage, panelSendItem,
  type ShopCategoryAdmin, type ShopCategoryAdminInput,
  type ShopProductAdmin, type ShopProductAdminInput, type ShopOptionAdmin,
} from '@/shared/services/adminService';
import { shopImg } from '@/shared/services/shopService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

type Tab = 'produtos' | 'categorias';
type SortState = { key: string; order: 'asc' | 'desc' };

/** comparador genérico pro sort client-side (número, string, null por último) */
function cmp(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base', numeric: true });
}

export default function CashShopAdminPage() {
  const { notify } = useToast();
  const [tab, setTab] = useState<Tab>('produtos');

  const [cats, setCats] = useState<ShopCategoryAdmin[]>([]);
  const [products, setProducts] = useState<ShopProductAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [catFilter, setCatFilter] = useState<number | 'all'>('all');
  const [q, setQ] = useState('');
  const [prodSort, setProdSort] = useState<SortState>({ key: 'id', order: 'desc' });
  const [catSort, setCatSort] = useState<SortState>({ key: 'ordem', order: 'asc' });

  const toggleSort = (set: React.Dispatch<React.SetStateAction<SortState>>) => (key: string) =>
    set((s) => ({ key, order: s.key === key && s.order === 'asc' ? 'desc' : 'asc' }));

  const [editingProd, setEditingProd] = useState<ShopProductAdmin | null>(null);
  const [creatingProd, setCreatingProd] = useState(false);
  const [sendingProd, setSendingProd] = useState<ShopProductAdmin | null>(null);
  const [editingCat, setEditingCat] = useState<ShopCategoryAdmin | null>(null);
  const [creatingCat, setCreatingCat] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, p] = await Promise.all([listShopCategoriesAdmin(), listShopProductsAdmin()]);
      setCats(Array.isArray(c) ? c : []);
      setProducts(Array.isArray(p) ? p : []);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const catName = useCallback(
    (id: number) => cats.find((c) => c.id === id)?.name || `#${id}`,
    [cats],
  );

  const filteredProds = useMemo(() => {
    let list = products;
    if (catFilter !== 'all') list = list.filter((p) => p.categoryId === catFilter);
    const t = q.trim().toLowerCase();
    if (t) list = list.filter((p) => (p.title || '').toLowerCase().includes(t) || String(p.id) === t);
    const dir = prodSort.order === 'asc' ? 1 : -1;
    const val = (p: ShopProductAdmin): unknown => {
      switch (prodSort.key) {
        case 'title': return p.title;
        case 'cat': return catName(p.categoryId);
        case 'price': return p.price ?? 0;
        case 'opts': return p.optionsCount ?? 0;
        case 'active': return p.active ?? 0;
        default: return p.id;
      }
    };
    return [...list].sort((a, b) => dir * cmp(val(a), val(b)));
  }, [products, catFilter, q, prodSort, catName]);

  const sortedCats = useMemo(() => {
    const dir = catSort.order === 'asc' ? 1 : -1;
    const val = (c: ShopCategoryAdmin): unknown => {
      switch (catSort.key) {
        case 'name': return c.name;
        case 'products': return c.products ?? 0;
        case 'id': return c.id;
        default: return c.ordem ?? 0;
      }
    };
    return [...cats].sort((a, b) => dir * cmp(val(a), val(b)));
  }, [cats, catSort]);

  const handleDeleteProd = async (id: number) => {
    if (!confirm('Remover esse produto da loja?')) return;
    try {
      await deleteShopProduct(id);
      notify('success', 'Produto removido');
      await fetchData();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const handleDeleteCat = async (id: number) => {
    if (!confirm('Remover essa categoria?')) return;
    try {
      await deleteShopCategory(id);
      notify('success', 'Categoria removida');
      await fetchData();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const prodCols: DataTableColumn<ShopProductAdmin>[] = [
    { key: 'id', label: 'ID', width: '60px', sortKey: 'id',
      cell: (p) => <span className="text-xs font-mono text-white/55">{p.id}</span> },
    { key: 'img', label: '', width: '52px',
      cell: (p) => p.image
        ? <img src={shopImg(p.image)} alt="" className="h-9 w-9 object-contain" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')} />
        : <Package size={18} className="text-white/20" /> },
    { key: 'title', label: 'Produto', width: '2fr', sortKey: 'title',
      cell: (p) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{p.title || '—'}</p>
          {p.description && <p className="text-[11px] text-white/45 truncate">{p.description}</p>}
        </div>
      ) },
    { key: 'cat', label: 'Categoria', width: '1.2fr', sortKey: 'cat',
      cell: (p) => <span className="text-xs text-white/70 truncate">{catName(p.categoryId)}</span> },
    { key: 'price', label: 'Preço (Cash)', width: '110px', sortKey: 'price',
      cell: (p) => <span className="text-sm font-bold text-[#00e1d7]">{(p.price ?? 0).toLocaleString('pt-BR')}</span> },
    { key: 'opts', label: 'Options', width: '80px', sortKey: 'opts',
      cell: (p) => (p.optionsCount ?? 0) > 0
        ? <span className="text-xs font-bold text-white/75">{p.optionsCount}</span>
        : <span className="text-xs text-white/30">—</span> },
    { key: 'active', label: 'Ativo', width: '70px', sortKey: 'active',
      cell: (p) => p.active === 1
        ? <span className="text-[10px] font-bold text-emerald-400">SIM</span>
        : <span className="text-[10px] font-bold text-white/35">NÃO</span> },
    { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
      cell: (p) => (
        <RowActions
          actions={[
            { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditingProd(p), variant: 'warning' },
            { label: 'Enviar p/ conta', icon: <Send size={13} />, onClick: () => setSendingProd(p) },
            { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDeleteProd(p.id), variant: 'danger' },
          ]}
        />
      ) },
  ];

  const catCols: DataTableColumn<ShopCategoryAdmin>[] = [
    { key: 'id', label: 'ID', width: '60px', sortKey: 'id',
      cell: (c) => <span className="text-xs font-mono text-white/55">{c.id}</span> },
    { key: 'name', label: 'Nome', width: '2fr', sortKey: 'name',
      cell: (c) => <span className="text-sm font-bold text-white truncate">{c.name || '—'}</span> },
    { key: 'ordem', label: 'Ordem', width: '80px', sortKey: 'ordem',
      cell: (c) => <span className="text-xs font-mono text-white/70">{c.ordem ?? 0}</span> },
    { key: 'products', label: 'Produtos', width: '90px', sortKey: 'products',
      cell: (c) => <span className="text-xs text-white/70">{c.products ?? 0}</span> },
    { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
      cell: (c) => (
        <RowActions
          actions={[
            { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditingCat(c), variant: 'warning' },
            { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDeleteCat(c.id), variant: 'danger' },
          ]}
        />
      ) },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <ShoppingCart className="text-[#00e1d7]" size={26} />
          Cash Shop
        </h1>
        <p className="text-sm text-white/65 mt-1">
          Loja cash do site — sem produto e sem categoria o menu não aparece pro jogador.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 mb-5 surface-card rounded-xl p-1 w-fit max-w-full">
        <TabBtn active={tab === 'produtos'} onClick={() => setTab('produtos')}
          icon={<Package size={14} />} label="Produtos" count={products.length} />
        <TabBtn active={tab === 'categorias'} onClick={() => setTab('categorias')}
          icon={<Tags size={14} />} label="Categorias" count={cats.length} />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {tab === 'produtos' ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full sm:w-56 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white cursor-pointer"
            >
              <option value="all">Todas as categorias</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto…"
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-white/35"
              />
            </div>
            <button onClick={() => setCreatingProd(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                         btn-primary transition cursor-pointer shrink-0">
              <Plus size={14} /> Novo Produto
            </button>
          </div>

          <DataTable<ShopProductAdmin>
            columns={prodCols}
            rows={filteredProds}
            rowKey={(p) => p.id}
            loading={loading}
            empty="Nenhum produto cadastrado"
            sort={prodSort}
            onSort={toggleSort(setProdSort)}
            mobileTitle={(p) => (
              <span className="flex items-center gap-2">{p.title || '—'} <span className="text-[10px] text-white/45 font-mono">#{p.id}</span></span>
            )}
            mobileSubtitle={(p) => `${catName(p.categoryId)} · ${(p.price ?? 0).toLocaleString('pt-BR')} Cash`}
            mobileActions={(p) => (
              <RowActions
                actions={[
                  { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditingProd(p), variant: 'warning' },
                  { label: 'Enviar p/ conta', icon: <Send size={13} />, onClick: () => setSendingProd(p) },
                  { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDeleteProd(p.id), variant: 'danger' },
                ]}
                align="left"
              />
            )}
          />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-white/65">{cats.length} categoria(s)</p>
            <button onClick={() => setCreatingCat(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                         btn-primary transition cursor-pointer">
              <Plus size={14} /> Nova Categoria
            </button>
          </div>

          <DataTable<ShopCategoryAdmin>
            columns={catCols}
            rows={sortedCats}
            rowKey={(c) => c.id}
            loading={loading}
            empty="Nenhuma categoria cadastrada"
            sort={catSort}
            onSort={toggleSort(setCatSort)}
            mobileTitle={(c) => (
              <span className="flex items-center gap-2">{c.name || '—'} <span className="text-[10px] text-white/45 font-mono">#{c.id}</span></span>
            )}
            mobileSubtitle={(c) => `Ordem ${c.ordem ?? 0} · ${c.products ?? 0} produto(s)`}
            mobileActions={(c) => (
              <RowActions
                actions={[
                  { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditingCat(c), variant: 'warning' },
                  { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDeleteCat(c.id), variant: 'danger' },
                ]}
                align="left"
              />
            )}
          />
        </>
      )}

      {(editingProd || creatingProd) && (
        <ProductEditor
          prod={editingProd}
          cats={cats}
          onClose={() => { setEditingProd(null); setCreatingProd(false); }}
          onSaved={async (created) => {
            await fetchData();
            if (created) { setCreatingProd(false); setEditingProd(created); }
            else { setEditingProd(null); setCreatingProd(false); }
          }}
        />
      )}

      {sendingProd && (
        <SendProductDialog prod={sendingProd} onClose={() => setSendingProd(null)} />
      )}

      {(editingCat || creatingCat) && (
        <CategoryEditor
          cat={editingCat}
          onClose={() => { setEditingCat(null); setCreatingCat(false); }}
          onSaved={async () => { await fetchData(); setEditingCat(null); setCreatingCat(false); }}
        />
      )}
    </div>
  );
}

/* ───────────────────────────── Enviar produto p/ conta ───────────────────── */

function SendProductDialog({ prod, onClose }: { prod: ShopProductAdmin; onClose: () => void }) {
  const { notify } = useToast();
  const [options, setOptions] = useState<ShopOptionAdmin[]>([]);
  const [optId, setOptId] = useState<number>(0);
  const [userNum, setUserNum] = useState('');
  const [qty, setQty] = useState('1');
  const [busy, setBusy] = useState(false);

  // Produtos com options entregam o item DA option (ItemIdOpt), igual ao buy
  useEffect(() => {
    if ((prod.optionsCount ?? 0) === 0) return;
    listShopOptionsAdmin(prod.id)
      .then((r) => {
        const act = (r || []).filter((o) => o.active === 1);
        setOptions(act);
        if (act.length) setOptId(act[0].id);
      })
      .catch(() => setOptions([]));
  }, [prod]);

  const send = async () => {
    const un = parseInt(userNum) || 0;
    const n = Math.max(1, parseInt(qty) || 1);
    if (!un) { notify('error', 'Informe o UserNum da conta'); return; }
    const opt = options.find((o) => o.id === optId);
    const baseItem = opt ? opt.itemIdOpt : prod.itemId;
    // AccountBind soma 4096 no itemId — mesmo comportamento da compra na loja
    const itemId = (prod.accountBind ?? 0) === 1 ? baseItem + 4096 : baseItem;
    setBusy(true);
    try {
      const res = await panelSendItem({
        userNum: un,
        itemId,
        itemOption: opt ? opt.itemOpt : (prod.itemOpt ?? 0),
        itemDuration: opt ? opt.durationId : (prod.durationId ?? 0),
        itemQty: n * Math.max(1, prod.itemCount ?? 1),
      });
      notify('success', res?.message || `Item enviado pra conta ${un}`);
      onClose();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao enviar');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Send size={16} /> Enviar p/ conta</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        {/* Dados mínimos do produto */}
        <div className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-black/30 p-3 mb-4">
          {prod.image
            ? <img src={shopImg(prod.image)} alt="" className="h-14 w-14 object-contain shrink-0" onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')} />
            : <Package size={28} className="text-white/20 shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">{prod.title || `Produto #${prod.id}`}</p>
            <p className="text-[11px] font-mono text-white/45">
              item {prod.itemId} · opt {prod.itemOpt ?? 0}{prod.durationId ? ` · dur ${prod.durationId}` : ''}
              {(prod.accountBind ?? 0) === 1 ? ' · BIND' : ''}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {options.length > 0 && (
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Option (variação)</label>
              <select
                value={optId} onChange={(e) => setOptId(Number(e.target.value))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white cursor-pointer"
              >
                {options.map((o) => <option key={o.id} value={o.id}>{o.title || `Option ${o.id}`}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="UserNum da conta" value={userNum} onChange={setUserNum} numeric mono />
            <Field label="Quantidade" value={qty} onChange={setQty} numeric />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={send} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Editor de produto ─────────────────────────── */

function ProductEditor({ prod, cats, onClose, onSaved }: {
  prod: ShopProductAdmin | null;
  cats: ShopCategoryAdmin[];
  onClose: () => void;
  onSaved: (created?: ShopProductAdmin) => Promise<void>;
}) {
  const { notify } = useToast();
  const [title, setTitle] = useState(prod?.title || '');
  const [description, setDescription] = useState(prod?.description || '');
  const [price, setPrice] = useState(String(prod?.price ?? 0));
  const [categoryId, setCategoryId] = useState<number>(prod?.categoryId ?? (cats[0]?.id ?? 0));
  const [image, setImage] = useState(prod?.image || '');
  const [itemId, setItemId] = useState(String(prod?.itemId ?? 0));
  const [itemOpt, setItemOpt] = useState(String(prod?.itemOpt ?? 0));
  const [durationId, setDurationId] = useState(String(prod?.durationId ?? 0));
  const [itemCount, setItemCount] = useState(String(prod?.itemCount ?? 1));
  const [accountBind, setAccountBind] = useState((prod?.accountBind ?? 0) === 1);
  const [active, setActive] = useState((prod?.active ?? 1) === 1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const doUpload = async (file: File) => {
    setUploading(true);
    try {
      const r = await uploadShopImage(file);
      setImage(r.filename);
      notify('success', `Imagem enviada: ${r.filename}`);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro no upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!categoryId) { notify('error', 'Escolha uma categoria'); return; }
    setBusy(true);
    try {
      const payload: ShopProductAdminInput = {
        categoryId,
        itemId: parseInt(itemId) || 0,
        itemOpt: Number(itemOpt) || 0,
        durationId: parseInt(durationId) || 0,
        price: parseInt(price) || 0,
        priceBonus: prod?.priceBonus ?? 0,
        image: image || undefined,
        description: description || undefined,
        title: title || undefined,
        active: active ? 1 : 0,
        itemCount: Math.max(1, parseInt(itemCount) || 1),
        accountBind: accountBind ? 1 : 0,
      };
      if (prod) {
        await updateShopProduct(prod.id, payload);
        notify('success', 'Produto atualizado');
        await onSaved();
      } else {
        const created = await createShopProduct(payload);
        notify('success', 'Produto criado — agora dá pra cadastrar as options');
        await onSaved(created);
      }
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{prod ? `Editar Produto #${prod.id}` : 'Novo Produto'}</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <div className="grid md:grid-cols-[1fr_180px] gap-5">
          <div className="space-y-3">
            <Field label="Nome" value={title} onChange={setTitle} placeholder="ex: Slot Cancel IG Pen" />
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Descrição</label>
              <textarea
                value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white resize-y"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Preço (Cash)" value={price} onChange={setPrice} numeric />
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Categoria</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(Number(e.target.value))}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white cursor-pointer"
                >
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Imagem (nome do arquivo na pasta /shop-img/)</label>
              <div className="flex gap-2">
                <input
                  value={image} onChange={(e) => setImage(e.target.value)} placeholder="ex: 33557263.png"
                  className="flex-1 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white font-mono"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/[0.06]
                             hover:bg-white/[0.12] text-white/85 transition cursor-pointer disabled:opacity-50 shrink-0"
                >
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                </button>
                <input
                  ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); }}
                />
              </div>
            </div>

            <div className="rounded-lg border border-white/[0.06] p-3 space-y-3">
              <p className="text-[11px] font-bold text-white/55 uppercase tracking-wide">Entrega do item</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="ItemId (jogo)" value={itemId} onChange={setItemId} numeric mono />
                <Field label="ItemOpt" value={itemOpt} onChange={setItemOpt} numeric mono />
                <Field label="DurationId" value={durationId} onChange={setDurationId} numeric mono />
                <Field label="Qtd por compra" value={itemCount} onChange={setItemCount} numeric />
              </div>
              <div className="flex items-center gap-5">
                <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
                  <input type="checkbox" checked={accountBind} onChange={(e) => setAccountBind(e.target.checked)} />
                  Account Bind
                </label>
                <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                  Ativo (visível na loja)
                </label>
              </div>
            </div>
          </div>

          {/* Preview da imagem */}
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Pré-visualização</label>
            <div className="h-44 rounded-lg border border-white/[0.06] bg-black/40 flex items-center justify-center overflow-hidden">
              {image
                ? <img key={image} src={shopImg(image)} alt="" className="max-h-40 max-w-full object-contain"
                       onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                : <Package size={40} className="text-white/15" />}
            </div>
          </div>
        </div>

        {/* Options — só depois que o produto existe (vínculo legado por ItemId do jogo) */}
        {prod ? (
          <OptionsEditor productId={prod.id} />
        ) : (
          <div className="mt-4 rounded-lg bg-sky-500/[0.06] border border-sky-500/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-sky-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-white/70 leading-relaxed">
              Salve o produto primeiro para cadastrar as <b>options</b> (variações — ex: Critc.Rate / Critc.Damage).
            </p>
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Fechar
          </button>
          <button onClick={save} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Options do produto ────────────────────────── */

function OptionsEditor({ productId }: { productId: number }) {
  const { notify } = useToast();
  const [options, setOptions] = useState<ShopOptionAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [itemIdOpt, setItemIdOpt] = useState('');
  const [itemOpt, setItemOpt] = useState('');
  const [durationId, setDurationId] = useState('0');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setOptions(await listShopOptionsAdmin(productId)); }
    catch { setOptions([]); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) { notify('error', 'Dê um título pra option'); return; }
    setAdding(true);
    try {
      await createShopOption(productId, {
        itemIdOpt: parseInt(itemIdOpt) || 0,
        itemOpt: Number(itemOpt) || 0,
        durationId: parseInt(durationId) || 0,
        title: title.trim(),
        active: 1,
      });
      setTitle(''); setItemIdOpt(''); setItemOpt('');
      notify('success', 'Option adicionada');
      await load();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setAdding(false); }
  };

  const toggle = async (o: ShopOptionAdmin) => {
    try {
      await updateShopOption(o.id, {
        itemIdOpt: o.itemIdOpt, itemOpt: o.itemOpt, durationId: o.durationId,
        title: o.title, description: o.description, active: o.active === 1 ? 0 : 1,
      });
      await load();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const remove = async (id: number) => {
    if (!confirm('Remover essa option?')) return;
    try {
      await deleteShopOption(id);
      notify('success', 'Option removida');
      await load();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  return (
    <div className="mt-4 rounded-lg border border-white/[0.06] p-3">
      <p className="text-[11px] font-bold text-white/55 uppercase tracking-wide mb-2">
        Options (variações do item) {loading && <Loader2 size={11} className="inline animate-spin ml-1" />}
      </p>

      {options.length > 0 && (
        <div className="space-y-1.5 mb-3 max-h-48 overflow-y-auto pr-1">
          {options.map((o) => (
            <div key={o.id} className="flex items-center gap-2 rounded-md bg-black/30 border border-white/[0.05] px-2.5 py-1.5">
              <button
                onClick={() => toggle(o)}
                title="Clique para ativar/desativar"
                className={`text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer shrink-0 ${
                  o.active === 1 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/[0.06] text-white/35'
                }`}
              >
                {o.active === 1 ? 'ON' : 'OFF'}
              </button>
              <span className="text-xs text-white/85 truncate flex-1">{o.title || `Option ${o.id}`}</span>
              <span className="text-[10px] font-mono text-white/40 shrink-0 hidden sm:block">
                item {o.itemIdOpt} · opt {o.itemOpt}{o.durationId ? ` · dur ${o.durationId}` : ''}
              </span>
              <button onClick={() => remove(o.id)} className="text-white/40 hover:text-red-400 cursor-pointer shrink-0">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      {!loading && options.length === 0 && (
        <p className="text-xs text-white/40 mb-3">Sem options — o produto entrega o ItemId/ItemOpt do cadastro acima.</p>
      )}

      <div className="space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ex: Critc.Rate / Critc.Damage)"
          className="w-full min-w-0 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-white" />
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
          <input value={itemIdOpt} onChange={(e) => setItemIdOpt(e.target.value.replace(/[^\d]/g, ''))} placeholder="ItemId"
            className="w-full min-w-0 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-white font-mono" />
          <input value={itemOpt} onChange={(e) => setItemOpt(e.target.value.replace(/[^\d]/g, ''))} placeholder="ItemOpt"
            className="w-full min-w-0 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-white font-mono" />
          <input value={durationId} onChange={(e) => setDurationId(e.target.value.replace(/[^\d]/g, ''))} placeholder="Duration"
            className="w-full min-w-0 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-white font-mono" />
          <button onClick={add} disabled={adding}
            className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-white/[0.06]
                       hover:bg-white/[0.12] text-white/85 transition cursor-pointer disabled:opacity-50">
            {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Editor de categoria ───────────────────────── */

function CategoryEditor({ cat, onClose, onSaved }: {
  cat: ShopCategoryAdmin | null; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const { notify } = useToast();
  const [name, setName] = useState(cat?.name || '');
  const [ordem, setOrdem] = useState(String(cat?.ordem ?? 0));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { notify('error', 'Dê um nome pra categoria'); return; }
    setBusy(true);
    try {
      const payload: ShopCategoryAdminInput = { name: name.trim(), ordem: parseInt(ordem) || 0 };
      if (cat) await updateShopCategory(cat.id, payload);
      else await createShopCategory(payload);
      notify('success', cat ? 'Categoria atualizada' : 'Categoria criada');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{cat ? 'Editar' : 'Nova'} Categoria</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Nome" value={name} onChange={setName} placeholder="ex: Costumes Body" />
          <Field label="Ordem (posição no dropdown)" value={ordem} onChange={setOrdem} numeric />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={save} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, numeric, mono, placeholder }:
  { label: string; value: string; onChange: (v: string) => void; numeric?: boolean; mono?: boolean; placeholder?: string }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input
        type="text" inputMode={numeric ? 'numeric' : undefined}
        value={value} placeholder={placeholder}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, count }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {icon} {label}
      <span className="text-[10px] text-white/45 font-mono">({count})</span>
    </button>
  );
}
